import { useAppSelector } from 'store/hooks';
import React, { CSSProperties } from 'react';
import { Box, Button, Grid, InputBase, Typography, styled } from '@mui/material';
import { Form } from 'formik';
import * as Yup from 'yup';
import { AwardsScreenShapes, AwardTemplate } from 'components/customers/models/Award';
import { valignStyle } from 'components/loyalty/models/PerkModel';
import { rgbaToHex } from 'lib/helpers';
import { MuiForm } from 'lib/Form';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { Column, Row } from 'ui/Flex';
import { PerkPreviewEditable, perkPreviewWidthByShape } from './visualisation/PerkPreviewEditable';
import { PerkGeneralFormData } from './PerkGeneralForm';
import { PerkSettingsFormData } from './PerkSettingsForm';
import { ExpiryLabelPreview } from '../ExpiryLabelPreview';
import { PerkVisualisationFormData } from 'components/perks/forms/PerkVisualisationForm';

export interface TemplateFormData {
    templateName: string;
    templateDescription: string;
}

const templateValidationSchema = Yup.object().shape({
    templateName: Yup.string().required('This field is required.'),
    templateDescription: Yup.string().required('This field is required.')
});

interface PerkReviewFormProps {
    data: PerkGeneralFormData & PerkSettingsFormData & PerkVisualisationFormData;
    open: boolean;
    label?: string;
    clickPrevious: () => void;
    submit: () => void;
    submitContinueAddTrigger?: () => void;
    isLoading: boolean;
    isLoadingContinueAddTrigger?: boolean;
    showButtonStepFive?: boolean;
    primaryActionBackgroundColour?: string;
    isTemplateMode?: boolean;
    templateInitialValues?: TemplateFormData;
    onTemplateSubmit?: (values: TemplateFormData) => void;
    readOnly?: boolean;
}

const PREFIX = 'PerkReviewForm';

const classes = {
    background: `${PREFIX}-background`
};

const StyledBox = styled(Box)(() => ({
    [`& .${classes.background}`]: {
        position: 'absolute',
        top: 0,
        width: '100%',
        height: '100%',
        left: 0,
        right: 0,
        bottom: 0
    }
}));

const StyledColumn = styled(Column)<{ awardScreenShape: AwardsScreenShapes; perkType: AwardTemplate }>(
    ({ theme, awardScreenShape, perkType }) => ({
        padding: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        position: 'relative',
        ...(awardScreenShape && (perkType === AwardTemplate.AD_HOC || perkType === AwardTemplate.CLUB)
            ? {
                  height: perkPreviewWidthByShape[awardScreenShape].height,
                  width: perkPreviewWidthByShape[awardScreenShape].width
              }
            : {
                  height: 320,
                  width: 480
              }),
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    })
);

export const PerkReviewForm: React.FC<PerkReviewFormProps> = props => {
    const {
        data,
        open,
        isLoading,
        submit,
        clickPrevious,
        label,
        showButtonStepFive,
        submitContinueAddTrigger,
        isLoadingContinueAddTrigger,
        primaryActionBackgroundColour,
        isTemplateMode,
        templateInitialValues,
        onTemplateSubmit,
        readOnly
    } = props;

    const { settings } = useAppSelector(state => state.settings);

    const awardsScreenShapeEnabled = React.useMemo(
        () => settings?.app?.awardsScreenBarcodePosition && settings.app.awardsScreenBarcodePosition !== '',
        [settings]
    );

    if (!open) {
        return null;
    }
    const textAlign: CSSProperties['textAlign'] = (
        data.align ?? 'center'
    ).toLowerCase() as CSSProperties['textAlign'];
    const stampsPerCard: number = !!data.stampsPerCard ? Number(data.stampsPerCard) : 6;
    const showExpiryLabelPreview =
        (data.perkType === AwardTemplate.AD_HOC || data.perkType === AwardTemplate.PSEUDO_CURRENCY) &&
        data.showExpiry;

    const showPerkImageFields =
        awardsScreenShapeEnabled &&
        (data.perkType === AwardTemplate.AD_HOC || data.perkType === AwardTemplate.CLUB)
            ? data.awardsScreenShape === AwardsScreenShapes.DOUBLE_RECTANGLE
            : true;

    const renderBox = () => (
        <StyledColumn
            perkType={data.perkType}
            awardScreenShape={awardsScreenShapeEnabled ? data.awardsScreenShape : undefined}
            valign={valignStyle[data.valign]}
        >
            {data.image && <img className={classes.background} src={data.image} alt="" />}
            {data.backgroundColor && (
                <div
                    className={classes.background}
                    style={{ backgroundColor: rgbaToHex(data.backgroundColor) }}
                />
            )}
            {showPerkImageFields && (
                <InputBase
                    fullWidth
                    disabled
                    multiline
                    maxRows={2}
                    style={{ fontSize: 45 }}
                    inputProps={{
                        style: {
                            textAlign: textAlign || 'start',
                            color: rgbaToHex(data.textColor),
                            WebkitTextFillColor: rgbaToHex(data.textColor)
                        }
                    }}
                    value={data.titleText}
                />
            )}
            {data.subtitle && showPerkImageFields && (
                <InputBase
                    fullWidth
                    disabled
                    multiline
                    maxRows={2}
                    style={{ fontSize: 29 }}
                    inputProps={{
                        style: {
                            textAlign: textAlign || 'start',
                            color: rgbaToHex(data.textColor),
                            WebkitTextFillColor: rgbaToHex(data.textColor)
                        }
                    }}
                    value={data.subtitle}
                />
            )}
            {data.subtitle2 && showPerkImageFields && (
                <InputBase
                    fullWidth
                    multiline
                    maxRows={2}
                    disabled
                    style={{ fontSize: 21 }}
                    inputProps={{
                        style: {
                            textAlign: textAlign || 'start',
                            color: rgbaToHex(data.textColor),
                            WebkitTextFillColor: rgbaToHex(data.textColor)
                        }
                    }}
                    value={data.subtitle2}
                />
            )}
            {showExpiryLabelPreview && showPerkImageFields && (
                <ExpiryLabelPreview
                    vertical={data.expiryLabelAnchorStyleVAlign}
                    horizontal={data.expiryLabelAnchorStyleAlign}
                    primaryActionBackgroundColour={primaryActionBackgroundColour}
                />
            )}
        </StyledColumn>
    );

    const renderPreview = () => (
        <Grid size={12}>
            <Row flex={1} align="center">
                {data.perkType === AwardTemplate.STAMP_CARD ? (
                    <Row gutter valign="flex-end">
                        <PerkPreviewEditable
                            label={
                                data.perkType === AwardTemplate.STAMP_CARD
                                    ? 'No purchase available'
                                    : undefined
                            }
                            {...data}
                            title={data.title}
                            stampsPerCard={stampsPerCard}
                            textAlign={textAlign}
                        />
                        {data.perkType === AwardTemplate.STAMP_CARD && (
                            <PerkPreviewEditable
                                label="Purchase available"
                                cardAvailable
                                {...data}
                                title={data.title}
                                stampsPerCard={stampsPerCard}
                                textAlign={textAlign}
                            />
                        )}
                    </Row>
                ) : (
                    renderBox()
                )}
            </Row>
        </Grid>
    );

    const renderActions = (templateSubmitForm?: () => Promise<void>) => (
        <Grid size={12}>
            <Row flex={1} gutter align="flex-end">
                <Button onClick={clickPrevious} color="primary" variant="outlined">
                    Previous
                </Button>
                {!!label && (
                    <LoadingButton
                        onClick={templateSubmitForm || submit}
                        color="primary"
                        variant="contained"
                        loading={isLoading}
                        disabled={isLoading}
                    >
                        {label || 'Create'}
                    </LoadingButton>
                )}
                {showButtonStepFive && (
                    <LoadingButton
                        onClick={submitContinueAddTrigger}
                        color="primary"
                        variant="contained"
                        loading={isLoadingContinueAddTrigger}
                        disabled={isLoadingContinueAddTrigger}
                    >
                        Next
                    </LoadingButton>
                )}
            </Row>
        </Grid>
    );

    if (readOnly) {
        return (
            <StyledBox>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Box paddingX={2}>
                            <Typography variant="h4">5. Review</Typography>
                            <Typography variant="body2">Preview how this template will look.</Typography>
                        </Box>
                    </Grid>
                    {renderPreview()}
                    <Grid size={12}>
                        <Row flex={1} gutter align="flex-end">
                            <Button onClick={clickPrevious} color="primary" variant="outlined">
                                Previous
                            </Button>
                        </Row>
                    </Grid>
                </Grid>
            </StyledBox>
        );
    }

    if (isTemplateMode && onTemplateSubmit && templateInitialValues) {
        return (
            <StyledBox>
                <MuiForm
                    initialValues={templateInitialValues}
                    validationSchema={templateValidationSchema}
                    onSubmit={onTemplateSubmit}
                    enableReinitialize
                >
                    {({ submitForm }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <Box paddingX={2}>
                                        <Typography variant="h4">5. Review</Typography>
                                        <Typography variant="body2">
                                            Review your Template, you can come back and change how it looks if
                                            you need to later.
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={12}>
                                    <Box paddingX={2} maxWidth={600}>
                                        <Grid container spacing={2}>
                                            <Grid size={12}>
                                                <TextFormField
                                                    name="templateName"
                                                    label={
                                                        <MuiFormLabel required>Template Name</MuiFormLabel>
                                                    }
                                                    description="Enter a name for your template."
                                                />
                                            </Grid>
                                            <Grid size={12}>
                                                <TextFormField
                                                    name="templateDescription"
                                                    label={
                                                        <MuiFormLabel required>
                                                            Template Description
                                                        </MuiFormLabel>
                                                    }
                                                    description="Enter a description for your template."
                                                    multiline
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                                {renderPreview()}
                                {renderActions(submitForm)}
                            </Grid>
                        </Form>
                    )}
                </MuiForm>
            </StyledBox>
        );
    }

    return (
        <StyledBox>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Box paddingX={2}>
                        <Typography variant="h4">5. Review</Typography>
                        <Typography variant="body2">
                            Review your Perk, you can come back and change how it looks if you need to later.
                        </Typography>
                    </Box>
                </Grid>
                {renderPreview()}
                {renderActions()}
            </Grid>
        </StyledBox>
    );
};
